import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { getNestedValue } from 'lib/helpers/exportPdf/lib/extractDataFromColDefs';
import {
  camelToSpaceCase,
  formatLargeNumber,
  ia,
  isPrimitiveValue,
  mString,
  snakeToTitleCase
} from 'lib/helpers/utility';

import { COLUMN_SAMPLE_SIZE } from './constants';

const nonFormattedFields = ['_id', 'procedure_code'];

export const generateHeaderName = (key) => {
  return camelToSpaceCase(snakeToTitleCase(key.replace(/__/g, '/').replace(/_id/g, ' #')));
};

const collectColumnSamples = (data) => {
  const samples = new Map();

  data.slice(0, COLUMN_SAMPLE_SIZE).forEach((row) => {
    Object.entries(row || {}).forEach(([key, value]) => {
      if (key === 'children') return;

      if (!samples.has(key)) samples.set(key, undefined);

      if (samples.get(key) === undefined && value !== null) samples.set(key, value);
    });
  });

  return samples;
};

export const generateColumnDefs = (data) => {
  if (!ia(data)) return [];

  const samples = collectColumnSamples(data);
  const keys = [...samples.keys()];

  const columnDefs = keys.map((key) => {
    const sampleValue = samples.get(key);
    const isNumber = !isNaN(parseFloat(sampleValue)) && isFinite(sampleValue);

    const headerName = generateHeaderName(key);

    const colDef = { headerName, field: key };

    // add group row
    if (keys.indexOf(key) === 0 && data.some((item) => ia(item.children))) {
      colDef.cellRenderer = 'agGroupCellRenderer';
    }

    if (sampleValue?.value !== undefined) {
      colDef.valueGetter = (params) => getNestedValue(params.data[key]?.value); // this fixes stuffs like sorting in ag grid or exporting data

      const cellRenderer = (params) => {
        const value = params.data[key]?.value ?? '-';
        const href = params.data[key]?.href;

        // add link if provided
        if (typeof value !== 'object') {
          if (!href) return value;

          return (
            <Link to={href} target="_blank">
              <span className="text-neutral-900 underline hover:opacity-80">{value}</span>
            </Link>
          );
        }

        const entries = Object.entries(value || {});

        return (
          <>
            {entries.map(([currentKey, currentValue], index) => {
              if (!href) {
                return (
                  <span key={currentKey}>
                    {!currentKey.endsWith('_') && typeof currentValue === 'number'
                      ? mString(currentValue)
                      : currentValue}
                    {index < entries.length - 1 && ' / '}{' '}
                  </span>
                );
              }

              return (
                <Fragment key={currentKey}>
                  <Link to={href?.[currentKey]} target="_blank">
                    <span className="text-neutral-900 underline hover:opacity-80">
                      {currentValue}
                    </span>
                  </Link>
                  {index < entries.length - 1 && ' / '}
                </Fragment>
              );
            })}
          </>
        );
      };

      // avoid missing row expanding
      if (!colDef.cellRenderer) {
        colDef.cellRenderer = cellRenderer;
      } else {
        colDef.cellRendererParams = { innerRenderer: cellRenderer };
      }
    }

    if (isNumber) {
      // Numeric identifiers/codes (procedure_code, *_id) must render verbatim — never with
      // thousands separators — even when the key ends with an underscore. Everything else
      // numeric is a measure: counts (trailing "_") via formatLargeNumber, otherwise mString.
      if (!nonFormattedFields.some((substring) => key.includes(substring))) {
        const formatNumeric = key.endsWith('_') ? formatLargeNumber : mString;
        colDef.valueFormatter = (params) =>
          typeof params.value === 'string' && isNaN(parseFloat(params.value))
            ? params.value
            : formatNumeric(params.value);
      }
    } else if (isPrimitiveValue(sampleValue)) {
      // capitalize values
      colDef.valueFormatter = (params) => {
        return camelToSpaceCase(snakeToTitleCase(params?.value?.toString())) || '-';
      };
    } else {
      colDef.valueFormatter = (params) => params.value || '-';
    }

    return colDef;
  });

  return columnDefs;
};
